import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Target, Brain, Users, MessageSquare, Code, Lightbulb, 
  CheckCircle2, ArrowRight, TrendingUp, Award
} from "lucide-react";

const SkillTests = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [myResults, setMyResults] = useState([]);
  const [generating, setGenerating] = useState(null);

  const skillIcons = {
    // Technical Skills
    "Programming Languages": Code,
    "Data Analysis": TrendingUp,
    "Web Development": Code,
    "Cloud Computing": Target,
    "Database Management": Brain,
    "DevOps": Target,
    "Cybersecurity": CheckCircle2,
    "AI/ML": Brain,
    "Mobile Development": Code,
    "System Design": Target,
    "Testing & QA": CheckCircle2,
    "Version Control": Code,
    
    // Soft Skills
    "Communication": MessageSquare,
    "Leadership": Users,
    "Teamwork": Users,
    "Problem Solving": Lightbulb,
    "Critical Thinking": Brain,
    "Emotional Intelligence": MessageSquare,
    "Adaptability": TrendingUp,
    "Time Management": CheckCircle2,
    "Negotiation": MessageSquare,
    "Presentation": MessageSquare,
    "Conflict Resolution": Users,
    "Mentoring": Users,
    
    // Cognitive
    "Logical Reasoning": Brain,
    "Verbal Comprehension": MessageSquare,
    "Spatial Reasoning": Target,
    "Numerical Ability": TrendingUp,
    "Memory Retention": Brain,
    "Processing Speed": TrendingUp,
    "Creative Thinking": Lightbulb,
    
    // Legacy
    "Domain Knowledge": Brain,
    "Decision Making": CheckCircle2
  };

  const skillColors = {
    // Technical Skills - Blue shades
    "Programming Languages": "blue",
    "Data Analysis": "blue",
    "Web Development": "blue",
    "Cloud Computing": "cyan",
    "Database Management": "blue",
    "DevOps": "cyan",
    "Cybersecurity": "blue",
    "AI/ML": "indigo",
    "Mobile Development": "blue",
    "System Design": "indigo",
    "Testing & QA": "cyan",
    "Version Control": "blue",
    
    // Soft Skills - Green/Purple shades
    "Communication": "green",
    "Leadership": "purple",
    "Teamwork": "green",
    "Problem Solving": "orange",
    "Critical Thinking": "red",
    "Emotional Intelligence": "pink",
    "Adaptability": "green",
    "Time Management": "purple",
    "Negotiation": "orange",
    "Presentation": "green",
    "Conflict Resolution": "purple",
    "Mentoring": "purple",
    
    // Cognitive - Purple/Indigo shades
    "Logical Reasoning": "purple",
    "Verbal Comprehension": "indigo",
    "Spatial Reasoning": "purple",
    "Numerical Ability": "indigo",
    "Memory Retention": "purple",
    "Processing Speed": "indigo",
    "Creative Thinking": "pink",
    
    // Legacy
    "Domain Knowledge": "indigo",
    "Decision Making": "pink"
  };

  useEffect(() => {
    if (user && token) {
      fetchData();
    }
  }, [user, token]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, resultsRes] = await Promise.all([
        axios.get(`${API}/skills/categories`),
        axios.get(`${API}/skills/my-results`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);
      
      setCategories(categoriesRes.data.categories);
      setMyResults(resultsRes.data.summary || []);
    } catch (error) {
      toast.error("Failed to load skill tests");
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async (skillName) => {
    try {
      setGenerating(skillName);
      const response = await axios.post(
        `${API}/skills/generate-test?skill_name=${encodeURIComponent(skillName)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Navigate to test page with test data
      navigate("/skill-test/take", {
        state: {
          testId: response.data.test_id,
          skillName: response.data.skill_name,
          questions: response.data.questions
        }
      });
    } catch (error) {
      toast.error("Failed to generate test");
    } finally {
      setGenerating(null);
    }
  };

  const getSkillResult = (skillName) => {
    return myResults.find(r => r.skill_name === skillName);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">Skill Assessment Tests</h1>
            <p className="text-gray-600 mb-4">
              Take personalized tests to evaluate your skills and identify areas for improvement
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate("/skill-test/gap-analysis")}
                variant="outline"
                className="gap-2"
              >
                <TrendingUp className="w-4 h-4" />
                View Skill Gap Analysis
              </Button>
            </div>
          </div>

          {/* Info Card */}
          <Card className="mb-8 border-l-4 border-l-indigo-500">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Award className="w-6 h-6 text-indigo-600 mt-1" />
                <div>
                  <h3 className="font-semibold text-lg mb-1">How It Works</h3>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>• Each test has 5 questions (mix of multiple-choice and scenario-based)</li>
                    <li>• Tests are AI-generated and personalized to your profile</li>
                    <li>• Earn points based on your score (up to 50 points per test)</li>
                    <li>• Track your progress and identify skill gaps</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Skill Categories - Grouped by Type */}
          <div className="space-y-8">
            {/* Technical Skills */}
            <div>
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Code className="w-6 h-6 text-blue-600" />
                </div>
                Technical Skills
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.filter(skill => [
                  "Programming Languages", "Data Analysis", "Web Development", "Cloud Computing",
                  "Database Management", "DevOps", "Cybersecurity", "AI/ML",
                  "Mobile Development", "System Design", "Testing & QA", "Version Control"
                ].includes(skill)).map((skillName) => {
                  const Icon = skillIcons[skillName] || Target;
                  const color = skillColors[skillName] || "gray";
                  const result = getSkillResult(skillName);
                  
                  return (
                    <Card key={skillName} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between mb-2">
                          <div className={`p-3 rounded-lg bg-${color}-100`}>
                            <Icon className={`w-6 h-6 text-${color}-600`} />
                          </div>
                          {result && (
                            <Badge variant={result.latest_score >= 70 ? "default" : "secondary"}>
                              {result.latest_score}%
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-lg">{skillName}</CardTitle>
                        <CardDescription>
                          {result ? (
                            <>
                              {result.attempts} attempt{result.attempts > 1 ? 's' : ''} • 
                              Last: {new Date(result.last_attempt).toLocaleDateString()}
                            </>
                          ) : (
                            "Not yet attempted"
                          )}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {result && (
                          <div className="mb-4">
                            <div className="flex justify-between text-sm mb-1">
                              <span>Progress</span>
                              <span className="font-semibold">{result.latest_score}%</span>
                            </div>
                            <Progress value={result.latest_score} className="h-2" />
                          </div>
                        )}
                        
                        <Button
                          onClick={() => handleStartTest(skillName)}
                          disabled={generating === skillName}
                          className="w-full gap-2"
                        >
                          {generating === skillName ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Generating Test...
                            </>
                          ) : result ? (
                            <>
                              Retake Test
                              <ArrowRight className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              Start Test
                              <ArrowRight className="w-4 h-4" />
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Soft Skills */}
            <div>
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-green-600" />
                </div>
                Soft Skills
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.filter(skill => [
                  "Communication", "Leadership", "Teamwork", "Problem Solving",
                  "Emotional Intelligence", "Adaptability", "Time Management",
                  "Negotiation", "Presentation", "Conflict Resolution", "Mentoring"
                ].includes(skill)).map((skillName) => {
                  const Icon = skillIcons[skillName] || Target;
                  const color = skillColors[skillName] || "gray";
                  const result = getSkillResult(skillName);
                  
                  return (
                    <Card key={skillName} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between mb-2">
                          <div className={`p-3 rounded-lg bg-${color}-100`}>
                            <Icon className={`w-6 h-6 text-${color}-600`} />
                          </div>
                          {result && (
                            <Badge variant={result.latest_score >= 70 ? "default" : "secondary"}>
                              {result.latest_score}%
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-lg">{skillName}</CardTitle>
                        <CardDescription>
                          {result ? (
                            <>
                              {result.attempts} attempt{result.attempts > 1 ? 's' : ''} • 
                              Last: {new Date(result.last_attempt).toLocaleDateString()}
                            </>
                          ) : (
                            "Not yet attempted"
                          )}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {result && (
                          <div className="mb-4">
                            <div className="flex justify-between text-sm mb-1">
                              <span>Progress</span>
                              <span className="font-semibold">{result.latest_score}%</span>
                            </div>
                            <Progress value={result.latest_score} className="h-2" />
                          </div>
                        )}
                        
                        <Button
                          onClick={() => handleStartTest(skillName)}
                          disabled={generating === skillName}
                          className="w-full gap-2"
                        >
                          {generating === skillName ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Generating Test...
                            </>
                          ) : result ? (
                            <>
                              Retake Test
                              <ArrowRight className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              Start Test
                              <ArrowRight className="w-4 h-4" />
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Cognitive Abilities */}
            <div>
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Brain className="w-6 h-6 text-purple-600" />
                </div>
                Cognitive Abilities
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.filter(skill => [
                  "Logical Reasoning", "Verbal Comprehension", "Spatial Reasoning",
                  "Numerical Ability", "Memory Retention", "Processing Speed", 
                  "Creative Thinking", "Critical Thinking"
                ].includes(skill)).map((skillName) => {
                  const Icon = skillIcons[skillName] || Target;
                  const color = skillColors[skillName] || "gray";
                  const result = getSkillResult(skillName);
                  
                  return (
                    <Card key={skillName} className="hover:shadow-lg transition-shadow">
                      <CardHeader>
                        <div className="flex items-start justify-between mb-2">
                          <div className={`p-3 rounded-lg bg-${color}-100`}>
                            <Icon className={`w-6 h-6 text-${color}-600`} />
                          </div>
                          {result && (
                            <Badge variant={result.latest_score >= 70 ? "default" : "secondary"}>
                              {result.latest_score}%
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-lg">{skillName}</CardTitle>
                        <CardDescription>
                          {result ? (
                            <>
                              {result.attempts} attempt{result.attempts > 1 ? 's' : ''} • 
                              Last: {new Date(result.last_attempt).toLocaleDateString()}
                            </>
                          ) : (
                            "Not yet attempted"
                          )}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        {result && (
                          <div className="mb-4">
                            <div className="flex justify-between text-sm mb-1">
                              <span>Progress</span>
                              <span className="font-semibold">{result.latest_score}%</span>
                            </div>
                            <Progress value={result.latest_score} className="h-2" />
                          </div>
                        )}
                        
                        <Button
                          onClick={() => handleStartTest(skillName)}
                          disabled={generating === skillName}
                          className="w-full gap-2"
                        >
                          {generating === skillName ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              Generating Test...
                            </>
                          ) : result ? (
                            <>
                              Retake Test
                              <ArrowRight className="w-4 h-4" />
                            </>
                          ) : (
                            <>
                              Start Test
                              <ArrowRight className="w-4 h-4" />
                            </>
                          )}
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>

            {/* Other Skills (Legacy) */}
            {categories.filter(skill => ["Domain Knowledge", "Decision Making"].includes(skill)).length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Target className="w-6 h-6 text-gray-600" />
                  </div>
                  Other Skills
                </h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {categories.filter(skill => ["Domain Knowledge", "Decision Making"].includes(skill)).map((skillName) => {
                    const Icon = skillIcons[skillName] || Target;
                    const color = skillColors[skillName] || "gray";
                    const result = getSkillResult(skillName);
                    
                    return (
                      <Card key={skillName} className="hover:shadow-lg transition-shadow">
                        <CardHeader>
                          <div className="flex items-start justify-between mb-2">
                            <div className={`p-3 rounded-lg bg-${color}-100`}>
                              <Icon className={`w-6 h-6 text-${color}-600`} />
                            </div>
                            {result && (
                              <Badge variant={result.latest_score >= 70 ? "default" : "secondary"}>
                                {result.latest_score}%
                              </Badge>
                            )}
                          </div>
                          <CardTitle className="text-lg">{skillName}</CardTitle>
                          <CardDescription>
                            {result ? (
                              <>
                                {result.attempts} attempt{result.attempts > 1 ? 's' : ''} • 
                                Last: {new Date(result.last_attempt).toLocaleDateString()}
                              </>
                            ) : (
                              "Not yet attempted"
                            )}
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          {result && (
                            <div className="mb-4">
                              <div className="flex justify-between text-sm mb-1">
                                <span>Progress</span>
                                <span className="font-semibold">{result.latest_score}%</span>
                              </div>
                              <Progress value={result.latest_score} className="h-2" />
                            </div>
                          )}
                          
                          <Button
                            onClick={() => handleStartTest(skillName)}
                            disabled={generating === skillName}
                            className="w-full gap-2"
                          >
                            {generating === skillName ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Generating Test...
                              </>
                            ) : result ? (
                              <>
                                Retake Test
                                <ArrowRight className="w-4 h-4" />
                              </>
                            ) : (
                              <>
                                Start Test
                                <ArrowRight className="w-4 h-4" />
                              </>
                            )}
                          </Button>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SkillTests;
